<template>
  <b-container fluid>
    <b-row class="search-container">
      <div :class="`col-12 ${shouldShowClearSearch ? 'col-md-8' : ''}`">
        <h5 class="search-title">{{ $t('findLicenseBy') }}</h5>
      </div>
      <div class="col-12 col-md-4 text-right" v-if="shouldShowClearSearch">
        <a href="#" class="clear-search" @click.stop.prevent="$emit('clearSearchClick')">
          {{ $t('clearSearch') }}
        </a>
      </div>
      <div class="col-12 col-md-4 order-md-1">
        <b-select
          @change="$emit('findLicenseByChange', $event)"
          :value="findLicenseByOption"
          :options="findLicenseByOptions"
          size="lg"
          class="search-options"
        />
      </div>
      <div class="col-12 col-md-5 order-md-2">
        <b-form-input
          @input="handleSearchInput"
          @keypress.native="handleKeypress"
          :value="searchInput"
          size="lg"
          class="search-input"
        />
      </div>
      <div class="col-12 advanced-search-container order-md-4" v-if="isAdvancedSearchVisible">
        <b-row>
          <div class="col-12 col-md-4">
            <b-select
              @change="$emit('orderDateChange', $event)"
              :value="dateOption"
              :options="dateOptions"
              size="lg"
              class="advanced-search-options"
            />
          </div>
          <div class="col-12 col-md-4">
            <b-row>
              <div class="col-6">
                <datepicker
                  input-class="form-control form-control-lg"
                  @selected="$emit('fromDateChange', $event)"
                  class="form-control form-control-lg"
                  :placeholder="$t('from')"
                  :value="fromDateValue"
                  :disabledDates="disabledDatesFrom"
                  :format="setDateFormat"
                />
              </div>
              <div class="col-6 to-datepicker">
                <datepicker
                  input-class="form-control form-control-lg"
                  @selected="$emit('toDateChange', $event)"
                  class="form-control form-control-lg"
                  :placeholder="$t('to')"
                  :value="toDateValue"
                  :disabledDates="disabledDatesTo"
                  :disabled="fromDateValue === ''"
                  :format="setDateFormat"
                />
              </div>
            </b-row>
          </div>
          <div class="col-12 col-md-4">
            <b-select
              :disabled="isFromAllAssetsPage"
              @change="$emit('licenseTypeChange', $event)"
              :value="licenseTypeOption"
              :options="licenseTypeOptions"
              size="lg"
              class="advanced-search-options-type"
            />
          </div>
        </b-row>
      </div>
      <div class="col-12 col-md-3 order-md-3">
        <b-button
          variant="outline-primary search-btn"
          size="lg"
          @click="$emit('onSearchClick')"
          :disabled="isSearchDisabled"
        >
          {{ $t('search') }}
        </b-button>
      </div>
      <div class="col-12 advanced-option-link-container order-md-4">
        <a href="#" @click.stop.prevent="handleAdvancedSearchButtonClick">
          {{ advancedOptionsLinkText }}
        </a>
      </div>
    </b-row>
  </b-container>
</template>

<script>
  import DatePicker from 'vuejs-datepicker'

  export default {
    name: 'Search',
    components: {
      'datepicker': DatePicker
    },
    data () {
      return {
        dateOptions: [
          { value: 'StartDate', text: this.$t('purchaseDate') },
          { value: 'ExpirationDate', text: this.$t('expirationDate') }
        ],
        licenseTypeOptions: [
          { value: 'active', text: this.$t('activeOnly') },
          { value: 'archived', text: this.$t('hiddenOnly') }
        ],
        datePickerFormat: 'MM/dd/yyyy'
      }
    },
    props: {
      findLicenseByOption: {
        type: String,
        default: 'licenseOrSerialNumber'
      },
      searchInput: {
        type: String,
        default: ''
      },
      dateOption: {
        type: String,
        default: 'orderDate'
      },
      licenseTypeOption: {
        type: String,
        default: 'all'
      },
      fromDateValue: {
        type: String,
        default: ''
      },
      toDateValue: {
        type: String,
        default: ''
      },
      isAdvancedSearchVisible: {
        type: Boolean,
        required: true
      },
      isFromAllAssetsPage: {
        type: Boolean,
        default: false
      },
      isSearchEmpty: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      findLicenseByOptions () {
        const options = [
          { value: 'SerialNumber', text: this.$t('licenseOrSerialNumber') },
          { value: 'ProductName', text: this.$t('productName') },
          { value: 'SAPSalesOrderNumber', text: this.$tc('orderNumber', 1) }
        ]
        if (!this.isFromAllAssetsPage) {
          options.push({ value: 'HostValue', text: this.$tc('hostId', 1) })
        }
        return options
      },
      advancedOptionsLinkText () {
        return this.$t(
          !this.isAdvancedSearchVisible
            ? 'showAdvancedSearchOptions'
            : 'hideAdvancedSearchOptions'
        )
      },
      isSearchDisabled () {
        if (!(this.searchInput.length < 3)) {
          if (this.fromDateValue && !this.toDateValue) return true
          else return false
        }
        return !this.fromDateValue || !this.toDateValue
      },
      disabledDatesFrom () {
        if (this.toDateValue !== '') {
          let date = new Date(this.toDateValue)
          date = date.setDate(date.getDate())
          return { from: new Date(date) }
        }
      },
      disabledDatesTo () {
        if (this.fromDateValue !== '') {
          let date = new Date(this.fromDateValue)
          date = date.setDate(date.getDate() + 1)
          return { to: new Date(date) }
        }
      },
      shouldShowClearSearch () {
        if (!this.isFromAllAssetsPage) return false
        else if (this.isSearchEmpty) return false
        return true
      },
      setDateFormat () {
        let locale = this.$store.getters.person.preferences.locale || 'en-US'
        const formats = {
          'zh-CN': 'yyyy/MM/dd',
          'zh-TW': 'yyyy/MM/dd',
          'jp-JP': 'yyyy/MM/dd',
          'ko-KR': 'yyyy/MM/dd',
          'es-CO': 'dd/MM/yyyy',
          'pt-BR': 'dd/MM/yyyy',
          'fr-FR': 'dd/MM/yyyy',
          'de-DE': 'dd/MM/yyyy',
          'en-US': 'MM/dd/yyyy'
        }
        locale = Object.keys(formats).includes(locale) ? locale : 'en-US'
        return formats[locale]
      }
    },
    methods: {
      handleAdvancedSearchButtonClick () {
        this.$emit('advancedSearchButtonClick', !this.isAdvancedSearchVisible)
      },
      handleSearchInput ($event) {
        this.$emit('searchInputChange', $event)
      },
      handleKeypress ({ key }) {
        if (key === 'Enter') {
          this.$emit('onSearchClick')
        }
      }
    }
  }
</script>

<style lang="scss">
  @import '../styles/variables.scss';

  select {
    font-size: 1rem;
  }
  .search-container {
    background-color: $primary;
    border-radius: 5px;
    color: $white;
    padding-top: 20px;
    padding-bottom: 20px;
    margin-top: 20px;
    margin-bottom: 20px;
  }
  .clear-search,
  .clear-search:hover {
    color: $white !important;
    margin-bottom: 15px;
    display: inline-block;
  }
  .search-input {
    font-size: 1rem;
  }
  .search-title {
    padding-bottom: 10px;
  }
  .search-options {
    margin-bottom: 20px;
  }
  .search-btn {
    border: 2px solid $white;
    color: $white;
    width: 100%;
    margin-top: 20px;
    font-weight: bold;
  }
  .search-btn:hover:not([disabled]),
  .search-btn:active,
  .search-btn:focus {
    background-color: $white !important;
    color: $primary !important;
    box-shadow: none;
  }
  .search-btn:hover {
    border-color: $white;
  }
  .advanced-option-link-container {
    text-align: center;
    margin-top: 20px;
  }
  .advanced-option-link-container > a,
  .advanced-option-link-container > a:hover {
    color: $white;
    text-decoration: underline;
  }
  .advanced-search-options {
    margin-top: 20px;
    margin-bottom: 20px;
  }
  .advanced-search-options-type {
    margin-top: 20px;
    margin-bottom: 0;
  }
  .vdp-datepicker.form-control.form-control-lg {
    padding: 0;
  }
  .to-datepicker .vdp-datepicker__calendar {
    right: 0;
  }
  .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover,
  .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).month:hover,
  .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).year:hover {
    background-color: $primary;
    color: $white;
    border: none;
  }
  .vdp-datepicker__calendar .cell.selected {
    background-color: $primary;
    color: $white;
  }
  .btn-outline-primary.disabled,
  .btn-outline-primary:disabled {
    color: inherit;
  }
  .help,
  .help:hover {
    color: $white;
    text-decoration: none;
  }

  @media (min-width: 768px) {
    .clear-search,
    .clear-search:hover {
      display: inline;
    }
    .search-options,
    .search-btn,
    .advanced-search-options,
    .advanced-search-options-type {
      margin: 0;
    }
    .advanced-option-link-container {
      text-align: left;
    }
    .advanced-search-container {
      padding-top: 20px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/Search.vue